import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../App';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Button
} from '@mui/material';
import {
  TrendingUp,
  Lightbulb,
  LocalFireDepartment,
  Person,
  Login
} from '@mui/icons-material';
import AppLogo from '../components/AppLogo';

const IdeasPage = () => {
  const navigate = useNavigate();
  const [allIdeas, setAllIdeas] = useState([]);
  const [displayedIdeas, setDisplayedIdeas] = useState([]);
  const [ideasOffset, setIdeasOffset] = useState(0);
  const [publishedApps, setPublishedApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const IDEAS_PER_PAGE = 8;

  useEffect(() => {
    loadIdeas();
    loadPublishedApps();
    checkUser();
  }, []);

  const handleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'twitter',
        options: {
          redirectTo: window.location.href
        }
      });
      if (error) throw error;
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const loadIdeas = async () => {
    try {
      const { data, error } = await supabase
        .from('product_ideas')
        .select('*')
        .order('priority', { ascending: true })
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      const ideas = data || [];
      setAllIdeas(ideas);
      setDisplayedIdeas(ideas.slice(0, IDEAS_PER_PAGE));
      setIdeasOffset(0);
    } catch (error) {
      console.error('Error loading ideas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPublishedApps = async () => {
    try {
      const { data, error } = await supabase
        .from('apps')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      console.log('Published apps query result:', { data, error });

      if (error) throw error;
      setPublishedApps(data || []);
    } catch (error) {
      console.error('Error loading published apps:', error);
    }
  };

  const refreshIdeas = () => {
    const newOffset = ideasOffset + IDEAS_PER_PAGE;
    if (newOffset >= allIdeas.length) {
      // 循环回到开头
      setDisplayedIdeas(allIdeas.slice(0, IDEAS_PER_PAGE));
      setIdeasOffset(0);
    } else {
      setDisplayedIdeas(allIdeas.slice(newOffset, newOffset + IDEAS_PER_PAGE));
      setIdeasOffset(newOffset);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return '#10b981';
    if (score >= 6) return '#f59e0b';
    return '#6b7280';
  };

  const getPainSeverityLabel = (score) => {
    if (score >= 8) return 'Critical';
    if (score >= 6) return 'High';
    if (score >= 4) return 'Medium';
    return 'Low';
  };

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a2e 100%)',
      pb: 4,
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        p: 2,
        pt: 3,
        maxWidth: '1400px',
        mx: 'auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <AppLogo size="responsive" />

        {/* User Avatar / Login */}
        {user ? (
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              p: 0.5,
              border: '2px solid rgba(99, 102, 241, 0.5)',
              '&:hover': {
                border: '2px solid rgba(99, 102, 241, 0.8)',
              }
            }}
          >
            <Avatar
              src={user.user_metadata?.avatar_url}
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#6366f1'
              }}
            >
              {user.email?.[0]?.toUpperCase() || <Person />}
            </Avatar>
          </IconButton>
        ) : (
          <Box
            onClick={() => setLoginModalOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
              }
            }}
          >
            <Login sx={{ fontSize: 16, color: 'white' }} />
            <Typography sx={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>
              Login
            </Typography>
          </Box>
        )}
      </Box>

      {/* Section Title */}
      <Box sx={{ px: 2, maxWidth: '1400px', mx: 'auto', mb: 3, textAlign: 'center' }}>
        <Typography sx={{
          color: '#9ca3af',
          fontSize: { xs: '18px', sm: '22px' },
          fontWeight: 600,
          fontFamily: '"SF Pro Rounded", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
          letterSpacing: '-0.02em',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1
        }}>
          Top pain points from
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'white' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '4px' }}>
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </Box>
        </Typography>
      </Box>

      {/* Ideas Grid */}
      <Box sx={{
        px: 2,
        maxWidth: '1400px',
        mx: 'auto',
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(4, 1fr)'
        },
        gap: 2
      }}>
        {displayedIdeas.map((idea) => (
          <Card
            key={idea.id}
            onClick={() => navigate(`/idea/${idea.id}`)}
            sx={{
              background: 'rgba(255,255,255,0.03)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(99, 102, 241, 0.5)',
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 40px rgba(99, 102, 241, 0.15)'
              }
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              {/* Category & Pain Score */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Chip
                  label={idea.category || 'General'}
                  size="small"
                  sx={{
                    background: 'rgba(99, 102, 241, 0.2)',
                    color: '#a5b4fc',
                    fontSize: '11px',
                    height: '24px'
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalFireDepartment sx={{
                    fontSize: 16,
                    color: getScoreColor(idea.pain_severity || 5)
                  }} />
                  <Typography sx={{
                    fontSize: '12px',
                    color: getScoreColor(idea.pain_severity || 5),
                    fontWeight: 'bold'
                  }}>
                    {getPainSeverityLabel(idea.pain_severity || 5)}
                  </Typography>
                </Box>
              </Box>

              {/* Pain Point (Title) */}
              <Typography variant="h6" sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                mb: 1,
                lineHeight: 1.3
              }}>
                {idea.painpoint || idea.problem_statement}
              </Typography>

              {/* Product Idea Name */}
              <Typography sx={{
                color: '#d1d5db',
                fontSize: '13px',
                mb: 1.5,
                lineHeight: 1.4
              }}>
                💡 {idea.name}
              </Typography>

              {/* Scores Bar */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>Value</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>{idea.value_score?.toFixed(1) || '0'}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(idea.value_score || 0) * 10}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#10b981'
                      }
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>Novelty</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#6b7280' }}>{idea.novelty_score?.toFixed(1) || '0'}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(idea.novelty_score || 0) * 10}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#6366f1'
                      }
                    }}
                  />
                </Box>
              </Box>

              {/* Evidence Count */}
              {idea.evidence_count > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
                  <TrendingUp sx={{ fontSize: 14, color: '#6b7280' }} />
                  <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                    {idea.evidence_count} evidence posts
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}

        {displayedIdeas.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Lightbulb sx={{ fontSize: 48, color: '#4b5563', mb: 2 }} />
            <Typography sx={{ color: '#6b7280' }}>
              No ideas yet. Check back soon!
            </Typography>
          </Box>
        )}
      </Box>

      {/* Refresh Button */}
      {allIdeas.length > IDEAS_PER_PAGE && (
        <Box sx={{ textAlign: 'center', mt: 3, mb: 4 }}>
          <Button
            onClick={refreshIdeas}
            variant="outlined"
            sx={{
              borderColor: 'rgba(255,255,255,0.2)',
              color: '#9ca3af',
              textTransform: 'none',
              px: 4,
              py: 1,
              borderRadius: 2,
              '&:hover': {
                borderColor: 'rgba(99, 102, 241, 0.5)',
                background: 'rgba(99, 102, 241, 0.1)'
              }
            }}
          >
            🔄 Show more pain points
          </Button>
        </Box>
      )}

      {/* Published Apps Section */}
      {publishedApps.length > 0 && (
        <>
          <Box id="apps-section" sx={{ px: 2, maxWidth: '1400px', mx: 'auto', mb: 3, mt: 6, textAlign: 'center' }}>
            <Typography sx={{
              color: '#9ca3af',
              fontSize: { xs: '18px', sm: '22px' },
              fontWeight: 600,
              fontFamily: '"SF Pro Rounded", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
              letterSpacing: '-0.02em'
            }}>
              🚀 Published Apps
            </Typography>
          </Box>

          <Box sx={{
            px: 2,
            maxWidth: '1400px',
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: 2
          }}>
            {publishedApps.map((app) => (
              <Card
                key={app.id}
                sx={{
                  background: 'rgba(255,255,255,0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(99, 102, 241, 0.5)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(99, 102, 241, 0.15)'
                  }
                }}
              >
                {/* Video Preview */}
                {app.thumbnail_url && app.thumbnail_url.endsWith('.webm') && (
                  <Box sx={{ height: 200, overflow: 'hidden' }}>
                    <video
                      src={app.thumbnail_url}
                      autoPlay
                      loop
                      muted
                      playsInline
                      onLoadedMetadata={(e) => { e.target.playbackRate = 4.0; }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                )}

                <CardContent sx={{ p: 2 }}>
                  {/* App Name */}
                  <Typography sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '15px',
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {app.app_name || app.project_name || 'Untitled'}
                  </Typography>

                  {/* Open Link */}
                  <Button
                    size="small"
                    onClick={() => window.open(`https://xpainkiller.com/apps/${app.project_name}`, '_blank')}
                    sx={{
                      color: '#a5b4fc',
                      textTransform: 'none',
                      fontSize: '13px',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        background: 'transparent',
                        color: '#c7d2fe'
                      }
                    }}
                  >
                    Open App →
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      )}

      {/* Login Modal */}
      <Dialog
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          color: '#18181b',
          pb: 0.5,
          pt: 3,
          fontSize: '18px',
          fontWeight: 600
        }}>
          Login to Continue
        </DialogTitle>
        <DialogContent sx={{ pb: 4, pt: 1 }}>
          <Button
            fullWidth
            onClick={handleLogin}
            sx={{
              py: 1.5,
              background: '#18181b',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              '&:hover': {
                background: '#27272a'
              }
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Continue with X
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default IdeasPage;
